import type { AppConfig } from '../types/config';

class ConfigLoader {
  private static instance: AppConfig | null = null;

  static async load(): Promise<AppConfig> {
    try {
      const response = await fetch('/config.json');
      
      if (!response.ok) {
        throw new Error(`Failed to load configuration: ${response.statusText}`);
      }

      const config = await response.json();
      
      // Validate required fields
      this.validateConfig(config);
      
      this.instance = config;
      return config;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Configuration error: ${error.message}`);
      }
      throw new Error('Failed to load configuration file');
    }
  }

  static get(): AppConfig {
    if (!this.instance) {
      throw new Error('Configuration not loaded. Call ConfigLoader.load() first.');
    }
    return this.instance;
  }

  /**
   * Check if a feature flag is enabled
   * @param featureName - Name of the feature to check
   * @returns true if the feature is enabled, false otherwise
   */
  static isFeatureEnabled(featureName: keyof NonNullable<AppConfig['features']>): boolean {
    if (!this.instance) {
      console.warn('Configuration not loaded, assuming feature is disabled:', featureName);
      return false;
    }
    
    return this.instance.features?.[featureName] ?? false;
  }

  private static validateConfig(config: any): asserts config is AppConfig {
    if (!config) {
      throw new Error('Configuration is empty');
    }

    if (!config.oauth) {
      throw new Error('Missing oauth configuration');
    }

    if (!config.oauth.tokenUrl || typeof config.oauth.tokenUrl !== 'string') {
      throw new Error('Missing or invalid oauth.tokenUrl');
    }

    if (!config.oauth.clientId || typeof config.oauth.clientId !== 'string') {
      throw new Error('Missing or invalid oauth.clientId');
    }

    if (!config.oauth.clientSecret || typeof config.oauth.clientSecret !== 'string') {
      throw new Error('Missing or invalid oauth.clientSecret');
    }

    if (!config.oauth.scope || typeof config.oauth.scope !== 'string') {
      throw new Error('Missing or invalid oauth.scope');
    }

    if (!config.api) {
      throw new Error('Missing api configuration');
    }

    if (!config.api.baseUrl || typeof config.api.baseUrl !== 'string') {
      throw new Error('Missing or invalid api.baseUrl');
    }

    // Validate features configuration if present
    if (config.features && typeof config.features !== 'object') {
      throw new Error('Invalid features configuration: must be an object');
    }

    if (config.features?.enhancedGeoJsonCoverage !== undefined && 
        typeof config.features.enhancedGeoJsonCoverage !== 'boolean') {
      throw new Error('Invalid features.enhancedGeoJsonCoverage: must be a boolean');
    }
  }
}

export default ConfigLoader;
